<template>
	<div class="popover-container" ref="container">
		<span
			v-if="hasTrigger"
			ref="trigger"
			class="popover-trigger"
			:class="{ open: isOpen }"
			@click="togglePopover"
			@keyup.enter="togglePopover"
			><slot
		/></span>
		<Teleport v-if="isOpen || prerender" to="body">
			<div
				class="pronto-popover"
				:class="contentClass"
				:style="cssVars"
				ref="popover"
				v-show="prerender ? isOpen : true"
			>
				<slot name="content" />
			</div>
		</Teleport>
	</div>
</template>

<script>
import { addEscapeListener, removeEscapeListener } from '@/helpers/escape'

let popoverId = 0

export const closeAllPopovers = (exceptIds) => {
	if (!Array.isArray(exceptIds)) {
		exceptIds = [exceptIds]
	}
	EventBus.$emit('close-popovers', exceptIds)
}

export default {
	name: 'Popover',
	props: {
		top: { type: Boolean, default: null },
		left: { type: Boolean, default: null },
		centered: { type: Boolean, default: null },
		open: { type: Boolean, default: null },
		keepOpenIds: { type: Array, default: () => [] },
		offsetY: { type: Number, default: 0 },
		fixedRight: { type: Number, default: null },
		prerender: { type: Boolean, default: false },
		contentClass: String,
		boundary: {
			type: Number,
			default: 15,
		},
	},
	emits: ['open', 'close'],
	expose: ['openPopover', 'closePopover', 'togglePopover', 'id'],
	data() {
		return {
			id: popoverId++,
			isOpen: false,
			cssVars: {},
		}
	},
	mounted() {
		window.addEventListener('resize', this.onWindowResize)
		if (this.hasTrigger) {
			document.addEventListener('click', this.onDocumentClick)
		}

		this.$onEventBus('close-popovers', (exceptIds = []) => {
			if (exceptIds?.includes(this.id)) return
			this.closePopover()
		})

		if (this.open) {
			this.openPopover()
		}
	},
	beforeUnmount() {
		this.closePopover()
		window.removeEventListener('resize', this.onWindowResize)
		document.removeEventListener('click', this.onDocumentClick)
	},
	computed: {
		hasTrigger() {
			return !!this.$slots.default
		},
	},
	watch: {
		open(open) {
			open ? this.openPopover() : this.closePopover()
		},
		async isOpen(open) {
			if (!open) return
			await this.$nextTick()
			this.adjustPosition()
		},
	},
	methods: {
		adjustPosition() {
			const { container, popover } = this.$refs
			const pRect = popover.getBoundingClientRect()
			const cRect = container.getBoundingClientRect()
			const { clientWidth, clientHeight } = document.documentElement
			// default popover position is down and right-aligned
			let top = cRect.y + cRect.height + this.offsetY
			let right = clientWidth - cRect.x - cRect.width
			if (this.centered) {
				right -= pRect.width / 2 - cRect.width / 2
			}
			const bottomOfPopover = top + pRect.height
			const isOffBottomOfScreen = bottomOfPopover > clientHeight - this.boundary
			if (this.top || isOffBottomOfScreen) {
				// popover will be off bottom of screen so
				// needs to be aligned above container
				top = cRect.y - pRect.height + this.offsetY
			}
			const leftSideOfPopover = clientWidth - right - pRect.width
			const isOffLeftOfScreen = leftSideOfPopover < this.boundary
			if (this.left || isOffLeftOfScreen) {
				// popover will be off left side of screen so
				// needs to be aligned to left side of container
				right = clientWidth - cRect.x - pRect.width
			}
			const isOffRightOfScreen = right < this.boundary
			if (isOffRightOfScreen) {
				// popover will be off right side of screen so
				// needs to be adjusted slightly to the left
				right = this.boundary
			}
			if (this.fixedRight) {
				right = this.fixedRight
			}
			// New top boundary check
			const isOffTopOfScreen = top < this.boundary
			if (isOffTopOfScreen) {
				// Adjust top to ensure it stays within the top boundary
				top = this.boundary
			}
			this.cssVars = {
				'--popover-top': `${top}px`,
				'--popover-right': `${right}px`,
			}
		},
		onDocumentClick(e) {
			if (!this.isOpen) return
			const { container, popover } = this.$refs
			if (
				!container ||
				!popover ||
				(!container.contains(e.target) && !popover.contains(e.target))
			) {
				this.closePopover()
			}
		},
		onWindowResize() {
			this.closePopover()
		},
		togglePopover() {
			this.isOpen ? this.closePopover() : this.openPopover()
		},
		async openPopover() {
			this.isOpen = true
			EventBus.$emit('close-popovers', [...this.keepOpenIds, this.id])
			this.escapeId = addEscapeListener(() => this.closePopover())
			this.$emit('open')
		},
		closePopover() {
			this.isOpen = false
			this.cssVars = {}
			removeEscapeListener(this.escapeId)
			this.$emit('close')
		},
	},
}
</script>

<style lang="scss" scoped>
.popover-container {
	width: max-content;
}
</style>

<style lang="scss">
@import 'src/sass/variables';

.pronto-popover {
	position: fixed;
	@include max-z;
	top: var(--popover-top);
	right: var(--popover-right);
}
</style>
